import { find, first, isEmpty, map, orderBy } from 'lodash-es'
import mapOptions from './googleMapOptions'
import boxNowLogo from '../../img/boxnow.png'
import { Loader, LoaderOptions } from 'google-maps'
import { MarkerClusterer } from '@googlemaps/markerclusterer'

export default class GoogleMapsService {
  /**
   * @constant
   * @type {LoaderOptions}
   * @default
   */
  options = {
    libraries: ['places', 'geometry']
  }

  googleMapsApiKey = ''
  loader = {}
  google = {}
  geocoder = {}
  bounds = null
  markers = []
  lastMarker = null
  availableSellingPoints = []
  markerCluster = null
  infowindow = null
  map = null
  useClusters = false
  transporterId = null

  constructor (googleMapsApiKey, useClusters = false, transporterId = null) {
    return (async () => {
      this.googleMapsApiKey = googleMapsApiKey
      this.loader = new Loader(this.googleMapsApiKey, this.options)
      this.google = await this.loader.load()
      this.infowindow = new this.google.maps.InfoWindow()
      this.geocoder = new this.google.maps.Geocoder()
      this.useClusters = useClusters
      this.bounds = await new this.google.maps.LatLngBounds()
      this.map = await new this.google.maps.Map(document.getElementById('map'), mapOptions)
      this.transporterId = transporterId
      return this
    })()
  }

  set transporterId (value) {
    this.transporterId = value
  }

  get transporterId () {
    return this.transporterId
  }

  get selectedTransporter () {
    return window.Vue.$store.getters.getSelectedTransporterObject
  }

  resolveClientLogo (point = null) {
    if (!this.selectedTransporter) {
      return null
    }
    switch (this.selectedTransporter.class_name) {
      case 'BOXNOW':
        return boxNowLogo
      case 'ACS':
        return point.image ?? ''
      default:
        return null
    }
  }

  createMap (sellingPoints) {
    this.availableSellingPoints = sellingPoints
  }

  async updateMap (sellingPoints, sellingPoint) {
    this.availableSellingPoints = sellingPoints
    try {
      this.markers = []
      this.bounds = await new this.google.maps.LatLngBounds()
      const sellingPointsForBound = (!isEmpty(this.availableSellingPoints)) ? this.availableSellingPoints : []
      $.each(sellingPointsForBound, (k, v) => {
        this.bounds.extend({ lng: v.longitude * 1.0, lat: v.latitude * 1.0 })
      })

      this.map = await new this.google.maps.Map(document.getElementById('map'), mapOptions)

      this.map.addListener('tilesloaded', async function () {
        await window.Vue.$store.dispatch('setGoogleMapLoading', false)
      })

      await this.map.fitBounds(this.bounds)

      if (this.useClusters) {
        await this.useMarkerCluster()
      } else {
        await this.useMarkersOnly()
      }

      // this for left side of points selection
      this.updateMarkerInfo(sellingPoint, this.google)
    } catch (ignored) {
    }
  }

  buildMarkerInfoStr (marker) {
    let infoList = `
        <li><b>Address:</b> ${marker.address}</li>
        <li><b>Postal Code:</b> ${marker.postalCode}</li>
        <li><b>Note:</b> ${marker.note}</li>
    `

    if (marker.phone !== '') {
      infoList += `<li><b>Phone:</b> ${marker.phone}</li>`
    }
    if (marker.workingHours !== '') {
      infoList += `<li><b>Working Hours:</b> ${marker.workingHours}</li>`
    }
    if (marker.image !== '') {
      infoList += `
        <li style="list-style-type: none;">
          <img id="markerImage" src="${marker.image}" alt="${marker.name}" style="max-width: 300px" width="auto" height="auto">
        </li>
      `
    }

    return `
        <div id="content">
          <div id="siteNotice">
              <h5>${marker.name}</h5>
              <ul>
                ${infoList}
              </ul>
          </div>
        </div>`
  }

  async manageMarkerPin (marker, pin, infoStr) {
    const windowWidth = window.innerWidth
    if (windowWidth > 767) {
      this.infowindow.setContent(infoStr)
    }
    this.infowindow.open(this.map, marker)

    if (pin !== null && pin !== undefined && !isEmpty(pin)) {
      const sellingPoints = window.Vue.$store.getters.getCachedFilteredSearchedClosestSmartPoints

      const sellingPointsMapped = map(sellingPoints, (sellingPoint) => {
        sellingPoint.checked = false
        return sellingPoint
      })

      const sellingPointsMappedFinal = map(sellingPointsMapped, point =>
        point.id === pin.id ? { ...point, checked: true } : point
      )

      await window.Vue.$store.dispatch('setCachedFilteredSearchedClosestSmartPoints', sellingPointsMappedFinal)

      pin.checked = true

      await window.Vue.$store.dispatch('setSelectedSmartPoint', pin)
    }

    if (this.lastMarker !== null && this.lastMarker.getAnimation() !== null) {
      this.lastMarker.setAnimation(null)
    }

    marker.setAnimation(this.google.maps.Animation.BOUNCE)
    this.lastMarker = marker
  }

  async useMarkerCluster () {
    await $.each(this.availableSellingPoints, (k, v) => {
      const infoStr = this.buildMarkerInfoStr(v)

      const marker = new this.google.maps.Marker({
        position: { lat: v.latitude * 1.0, lng: v.longitude * 1.0 },
        title: v.name,
        icon: this.resolveClientLogo(v)
      })
      marker.addListener('click', async () => {
        await this.manageMarkerPin(marker, v, infoStr)
      })

      this.infowindow.addListener('closeclick', () => {
        this.lastMarker.setAnimation(null)
      })
      this.markers.push(marker)

      this.markerCluster = new MarkerClusterer({ map: this.map, markers: this.markers })
    })
  }

  async useMarkersOnly () {
    await $.each(this.availableSellingPoints, (k, v) => {
      const infoStr = this.buildMarkerInfoStr(v)

      const marker = new this.google.maps.Marker({
        position: { lat: v.latitude * 1.0, lng: v.longitude * 1.0 },
        title: v.name,
        map: this.map,
        icon: this.resolveClientLogo(v)
      })

      marker.addListener('click', async () => {
        await this.manageMarkerPin(marker, v, infoStr)
      })

      this.infowindow.addListener('closeclick', () => {
        this.lastMarker.setAnimation(null)
      })
      this.markers.push(marker)
    })
  }

  async findClosest (input, sellingPoints, maxResults) {
    await window.Vue.$store.dispatch('setGoogleMapLoading', true)
    this.availableSellingPoints = sellingPoints
    const { places } = await this.google.maps.places.Place.searchByText({
      textQuery: input,
      fields: ['location'],
      locationBias: this.bounds.getCenter()
    })
    if (!places || places.length === 0) {
      return
    }
    this.map.addListener('tilesloaded', async function () {
      await window.Vue.$store.dispatch('setGoogleMapLoading', false)
    })
    await window.Vue.$emit('findClosest', {
        sellingPoints: await this.sortSellingPointsByClosest(first(places).location),
      maxResults: maxResults
      })
  }

  async initMapPoints (sellingPoints) {
    this.availableSellingPoints = sellingPoints
    await window.Vue.$store.dispatch('setCachedFilteredSearchedClosestSmartPoints', sellingPoints)

    this.bounds = await new this.google.maps.LatLngBounds()
    await $.each(this.availableSellingPoints, (k, v) => {
      this.bounds.extend({ lng: v.longitude * 1.0, lat: v.latitude * 1.0 })
    })
    this.map = await new this.google.maps.Map(document.getElementById('map'), mapOptions)
    await this.map.fitBounds(this.bounds)

    await window.Vue.$store.dispatch('setClosestSmartPoints', await this.sortSellingPointsByClosest({
      lat: first(this.availableSellingPoints).latitude,
      lng: first(this.availableSellingPoints).longitude
    }))
  }

  async initMap () {
    const map = await new this.google.maps.Map(document.getElementById('map'), mapOptions)
    await this.updateMap()
    window.map = map
  }

  updateMarkerInfo (sellingPoint, google) {
    if (sellingPoint !== null && sellingPoint !== undefined && !isEmpty(sellingPoint)) {
      const sellingPointName = sellingPoint.name
      const markerFound = find(this.markers, (marker) => {
        return marker.title === sellingPointName
      })

      let infoList = `<li><b>Address:</b> ${sellingPoint.address}</li>
                      <li><b>Postal Code:</b> ${sellingPoint.postalCode}</li>
                      <li><b>Note:</b> ${sellingPoint.note}</li>`

      if (sellingPoint.phone !== '') {
        infoList += `<li><b>Phone:</b> ${sellingPoint.phone}</li>`
      }
      if (sellingPoint.workingHours !== '') {
        infoList += `<li><b>Working Hours:</b> ${sellingPoint.workingHours}</li>`
      }
      if (sellingPoint.image !== '') {
        infoList += `<li style="list-style-type: none;">
                    <img id="markerImage" src="${sellingPoint.image}" alt="${sellingPoint.name}" style="max-width: 300px" width="auto" height="auto">
                   </li>`
      }

      const infoStr = `<div id="content">
          <div id="siteNotice">
              <h5>${sellingPoint.name}</h5>
              <ul>
                   ${infoList}
              </ul>
          </div>
      </div>`

      const windowWidth = window.innerWidth
      if (windowWidth > 767) {
        this.infowindow.setContent(infoStr)
      }
      this.infowindow.open(this.map, markerFound)

      if (this.lastMarker !== null && this.lastMarker.getAnimation() !== null) {
        this.lastMarker.setAnimation(null)
      }

      markerFound.setAnimation(google.maps.Animation.BOUNCE)
      this.lastMarker = markerFound

      this.map.setZoom(16)
    }
  }

  async sortSellingPointsByClosest (from) {
    const distances = []
    if (isEmpty(this.availableSellingPoints)) {
      this.availableSellingPoints = []
    }
    for (const [i, sellingPoint] of Object.entries(this.availableSellingPoints)) {
      const to = new this.google.maps.LatLng({
        lat: sellingPoint.latitude,
        lng: sellingPoint.longitude
      })
      distances.push({
        sellingPoint: sellingPoint,
        distance: this.google.maps.geometry.spherical.computeDistanceBetween(from, to)
      })
    }
    return orderBy(distances, ['distance'], ['asc'])
  }

  async setCurrentUserLocation () {
    // Try HTML5 geolocation.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          await this.geocoder.geocode({ location: pos }, async (results, status, infowindow) => {
            if (status === 'OK') {
              window.Vue.$store.dispatch('setCurrentUserGeolocation', results[0].formatted_address)
            } else {
              window.Vue.$store.dispatch('setCurrentUserGeolocationError', 'Geocode was not successful for the following reason: ' + status)
            }
          })
        },
        async (positionError) => {
          window.Vue.$store.dispatch('setCurrentUserGeolocationError', positionError)
          this.handleLocationError(true, this.infowindow, this.bounds.getCenter())
        }
      )
    } else {
      window.Vue.$store.dispatch('setCurrentUserGeolocationError', 'Browser doesn\'t support' +
        ' Geolocation')
      this.handleLocationError(false, this.infowindow, this.bounds.getCenter())
    }
  }

  handleLocationError (browserHasGeolocation, infoWindow, pos) {
    infoWindow.setPosition(pos)
    infoWindow.setContent(
      browserHasGeolocation
        ? 'Error: The Geolocation service failed.'
        : 'Error: Your browser doesn\'t support geolocation.'
    )
    if (this.map) {
      infoWindow.open(this.map)
    }
  }
}
